// PHA-1168: cell formatting and style resolution for custom views. Pure, so the
// builder preview, the Patient List and the Billing Report render a value the
// same way.
import type {
  CustomViewFieldType,
  CustomViewManifest,
  CustomViewRow,
  CustomViewStyle,
} from '../../services/api';

const pad = (n: number) => String(n).padStart(2, '0');

/** YYYY-MM-DD (a calendar date, no timezone) -> MM/DD/YYYY. */
const formatIsoDate = (value: string): string => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  return m ? `${m[2]}/${m[3]}/${m[1]}` : value;
};

export const formatCell = (value: unknown, type: CustomViewFieldType): string => {
  if (value === null || value === undefined || value === '') return '—';
  switch (type) {
    case 'date':
      return formatIsoDate(String(value));
    case 'datetime': {
      const d = new Date(String(value));
      if (Number.isNaN(d.getTime())) return String(value);
      return `${pad(d.getMonth() + 1)}/${pad(d.getDate())}/${d.getFullYear()}`;
    }
    case 'bool':
      return value ? 'Yes' : 'No';
    case 'int':
      return typeof value === 'number' ? String(Math.round(value)) : String(value);
    case 'number': {
      const n = typeof value === 'number' ? value : Number(value);
      if (Number.isNaN(n)) return String(value);
      return Number.isInteger(n) ? String(n) : n.toFixed(1);
    }
    case 'text_list':
      return Array.isArray(value) ? (value.length ? value.join(', ') : '—') : String(value);
    default:
      return String(value);
  }
};

// Light tints readable under dark text in both the builder and the grid. Inline
// colors rather than utility classes: the app's Tailwind is v2 from a CDN and has
// no orange.
export const STYLE_COLORS: Record<CustomViewStyle['color'], { bg: string; fg: string; radix: 'red' | 'orange' | 'yellow' | 'green' | 'blue' | 'gray' }> = {
  red: { bg: '#fdecec', fg: '#a61b1b', radix: 'red' },
  orange: { bg: '#fff1e3', fg: '#9a4a00', radix: 'orange' },
  yellow: { bg: '#fff9db', fg: '#7a5c00', radix: 'yellow' },
  green: { bg: '#e9f7ee', fg: '#1d6b3a', radix: 'green' },
  blue: { bg: '#e8f1fd', fg: '#1d4f91', radix: 'blue' },
  gray: { bg: '#f1f3f5', fg: '#3e4651', radix: 'gray' },
};

export interface ResolvedRowStyle {
  rowBg?: string;
  cellBg: Record<string, string>;
  badges: Record<string, { text: string; color: CustomViewStyle['color'] }[]>;
  rowBadges: { text: string; color: CustomViewStyle['color'] }[];
}

/** Which highlights and badges apply to a row, from the ids the server matched. */
export const resolveRowStyle = (row: CustomViewRow, manifest: CustomViewManifest): ResolvedRowStyle => {
  const out: ResolvedRowStyle = { cellBg: {}, badges: {}, rowBadges: [] };
  const byId = new Map(manifest.styles.map((s) => [s.id, s]));
  for (const id of row._styles || []) {
    const rule = byId.get(id);
    if (!rule) continue;
    const color = STYLE_COLORS[rule.color] || STYLE_COLORS.gray;
    if (rule.style === 'badge') {
      const badge = { text: rule.text || '', color: rule.color };
      if (rule.target === 'cell' && rule.field) {
        (out.badges[rule.field] = out.badges[rule.field] || []).push(badge);
      } else {
        out.rowBadges.push(badge);
      }
    } else if (rule.target === 'cell' && rule.field) {
      out.cellBg[rule.field] = out.cellBg[rule.field] || color.bg;
    } else if (!out.rowBg) {
      // First matching row rule wins, matching the order the admin wrote them.
      out.rowBg = color.bg;
    }
  }
  return out;
};

/** A CSV the browser can download, for a view rendered page-by-page (Patient List). */
export const manifestToCsv = (manifest: CustomViewManifest, records: CustomViewRow[]): string => {
  const quote = (s: string) => (/[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s);
  const header = manifest.columns.map((c) => quote(c.label)).join(',');
  const lines = records.map((row) =>
    manifest.columns
      .map((c) => {
        const v = row[c.key];
        if (v === null || v === undefined) return '';
        // Keep leading zeros in Excel, like the standard exports.
        if (c.key === 'mrn') return quote(`="${String(v)}"`);
        if (c.type === 'text_list' && Array.isArray(v)) return quote(v.join('; '));
        if (c.type === 'date' || c.type === 'datetime') return quote(formatCell(v, c.type));
        return quote(String(v));
      })
      .join(','),
  );
  return [header, ...lines].join('\n');
};

export const downloadCsv = (csv: string, fileName: string) => {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * The Patient List controls that still apply on top of a custom view. The view's
 * own filters decide WHO is in it; the page's Status / Stage / Consent / Tier
 * selects would silently intersect with them (Status defaults to "Onboarded", so
 * a view of chart-review patients would come back empty), and the builder's
 * preview never applies them. Search, "Appointment today" and the calendar
 * import narrow on purpose and stay.
 */
export const customViewGridFilters = (gridFilters: Record<string, unknown>): Record<string, unknown> => {
  const out: Record<string, unknown> = {};
  if (gridFilters.appointment_today !== undefined) out.appointment_today = gridFilters.appointment_today;
  if (gridFilters.patient_ids !== undefined) out.patient_ids = gridFilters.patient_ids;
  return out;
};

export const safeFileName = (name: string) =>
  name.trim().replace(/[^A-Za-z0-9 _-]+/g, '').replace(/\s+/g, '_').slice(0, 60) || 'view';
